import React, { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { BuildingStorefrontIcon, GlobeAltIcon, XCircleIcon } from "@heroicons/react/24/outline";

import { StandardDialog } from "./modals";
import SARA from '../images/SARA.png'
import VisibilitySara from '../images/visibility_illustration.png'
import VisiteSara from '../images/visite_illustration.png'
import wink from '../images/SARA_Expression/wink.png'
import PrimaryBtn from "./elements/Buttons/PrimaryBtn";
import { tour } from "../configTour";
import { selectUserInfo } from "../features/users/userInfoSlice";
import { PRODUCT_KEY, VALIDATION_URL } from "../constants";
import { toggleLaunch } from "../features/tour/tourSlice";
import FormBtn from "./elements/Buttons/FormBtn";
import { FormCardInput } from "./FormCard";
import { closeMessageWebsite } from "../features/websites/websitesSlice";
import { api } from "../api";
import ToastAction from "./ToastAction";

/**
 * @param {{ title: string, children: JSX.Element, open?: boolean, reOpen: false }}
 * @returns {JSX.Element}
 */
export const SaraPresentation = ({ title, open = true, reOpen }) => {
  const [show, setShow] = useState(JSON.parse(localStorage.getItem(title)) ?? open)

  useEffect(() => {
    localStorage.getItem(title) ? null : setShow(true)
  }, [reOpen])

  const _close = useCallback(() => {
    setShow(false)
    document.querySelector('#permanent_close').checked && localStorage.setItem(title, JSON.stringify(false))
  }, [])

  const _takeAppointment = () => {
    window.open('https://calendly.com/azatys/onboarding', '_blank')
  }

  const key = title.split(' ').at(-1)

  const _getIllustration = () => {
    if (key === 'TRAFIC') return VisiteSara
    return VisibilitySara
  }

  return (
    <StandardDialog open={show} onClose={_close}>
      <div className="bg-white rounded-3xl flex justify-between items-center">
        <div className="text-center space-y-8 min-w-3xl">
          <img src={_getIllustration()} alt={`Explication ${key} SARA`} />
          <footer className="flex flex-col items-center space-y-3 mx-auto max-w-xs">
            <label htmlFor="permanent_close" className="flex items-center space-x-2 text-sm text-gray-500 cursor-pointer">
              <input type="checkbox" name="permanent_close" id="permanent_close" className="checkbox checkbox-sm checkbox-accent peer" />
              <span className="peer-checked:text-general-teal">Ne plus me rappeler</span>
            </label>
            <div className="flex items-center space-x-3">
              <div className="max-w-[300px]">
                <PrimaryBtn onClick={_takeAppointment} label="Prendre un RDV" />
              </div>
              <div className="max-w-[300px]">
                <button onClick={_close} className='btn py-3 uppercase tracking-wide font-medium text-sm bg-gray-500 hover:bg-gray-600 transition-colors hover:border-none disabled:text-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed'>
                  Fermer
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </StandardDialog>
  );
};

/**
 * PREVIEW BUTTON SARA THEMATIC
 * @param {{title: string, open: boolean, setOpen: (v: boolean) => void}} props 
 * @returns {JSX.Element}
 */
export const PreviewSaraPresentation = ({ title, open, setOpen }) => <button onClick={() => {
  localStorage.removeItem(title)
  open && setOpen(false)
  !open && setOpen(true)
}} className="absolute top-0 -left-5 lg:left-5 text-xs flex flex-col space-y-1 items-center justify-center space-x-1 text-gray-600 transition-colors hover:underline hover:text-teal-800 px-4 py-2 rounded-full w-44">
  <img src={wink} alt="Sara thematique" className="h-7 w-7 lg:h-16 lg:w-16" />
  <span>Revoir l'explication de cette thématique</span>
</button>

/**
 * WELCOMEAPP COMPONENT
 * @param {{ open: boolean }} props 
 * @returns {JSX.Element}
 */
export const WelcomeApp = ({ open }) => {
  const history = useHistory()
  const userContext = useSelector(selectUserInfo)
  const [show, setShow] = useState(open)


  const _close = useCallback(() => {
    localStorage.setItem(`${userContext?.user?.company_id}_welcome`, 'Done')
    setShow(false)
    history.push('/account/forfait')
  }, [])
  return (
    <StandardDialog open={show} onClose={() => { }}>
      <div className="bg-white rounded-3xl flex justify-between items-center">
        <img src={SARA} alt="" className="h-96" />
        <div className="text-left px-5 space-y-8 max-w-2xl flex flex-col justify-between min-h-[380px]">
          <h2 className="font-bold text-general-blue text-3xl">Bienvenue au sein de ma plateforme de pilotage !</h2>
          <p className="text-gray-500 font-semibold h-full text-justify">
            Si vous avez créé votre compte, c’est que votre site
            web est important pour vous et que vous voulez
            améliorer ses performances ou suivre de plus près
            ses résultats. <br /> <br />

            <span className="font-bold text-[18px]">
              Pour commencer: <span className='text-general-yellow'>Choisissez votre forfait</span>
            </span>
            <br /> <br />
            <span className="text-general-blue font-bold text-[18px]">N'oubliez pas, votre 1<sup>er</sup> mois est offert</span>
          </p>
          <footer className="flex justify-end">
            <div className="w-[280px] animate-bounce hover:animate-none">
              <PrimaryBtn onClick={_close} label="C'est parti 🎉!" />
            </div>
          </footer>
        </div>
      </div>
    </StandardDialog>
  );
};

/**
 * SAVEWEBSITEMODAL COMPONENT
 * @param {{ open: boolean, goNext: (b: boolean) => void }} props 
 * @returns {JSX.Element}
 */
export const SaveWebsiteModal = ({ open, goNext }) => {
  const dispatch = useDispatch();
  const userContext = useSelector(selectUserInfo)


  const [show, setShow] = useState(open)
  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm(
    {
      shouldFocusError: true,
    }
  );

  const _close = useCallback(() => {
    setShow(false)
  }, [])

  const _saveSite = useCallback(async (site) => {
    setLoading(true)
    const idLoading = toast.loading("Enregistrement de votre site en cours...")
    try {
      const body = { site_url: "", title: "", company_id: userContext?.user?.company_id }
      const { id } = await api.sites().create(body)
      await api.sites().update({ ...site, website_id: id })
      dispatch(closeMessageWebsite())
      _close()
      toast.dismiss(idLoading)
      setLoading(false)
      goNext(true)
    } catch (error) {
      console.log(error)
      toast.dismiss(idLoading)
      setLoading(false)
      toast.custom(t =>
        <ToastAction
          toastRef={t}
          label='Oups !!'
          description="Nous avons rencontré un problème lors de la création de votre site. Veuillez réessayer SVP !"
          saraExpression="sorry"
        />
      )
    }
  }, [userContext?.user?.company_id])

  return (
    <StandardDialog open={show} onClose={() => { }}>
      <form onSubmit={handleSubmit(_saveSite)} className="bg-white rounded-3xl flex justify-between items-center relative">
        <button type='button' className="absolute -top-3 right-2" onClick={_close}>
          <XCircleIcon className="h-7 w-7 text-gray-500 hover:text-gray-600 transition-colors" />
        </button>
        <img src={SARA} alt="" className="h-96" />
        <div className="text-left px-5 space-y-8 max-w-2xl flex flex-col justify-between min-h-[380px] pb-3">
          <h2 className="font-bold text-general-blue text-2xl">Merci d'avoir rejoint nos utilisateurs !</h2>
          <p className="text-gray-500 font-semibold h-full text-sm text-justify">
            Pour me permettre de commencer à analyser vos données, <span className='text-general-yellow'>Inscrivez les informations concernant votre site web.</span> <br /> <br />
          </p>
          <section className="space-y-2">
            <FormCardInput
              autoFocus
              placeholder='Ex: Azatys'
              label="Nom"
              name="title"
              rules={{ maxLength: 50, required: true }}
              errorType={errors?.title?.type}
              register={register}
              icon={<BuildingStorefrontIcon
                className="w-4 h-4 text-gray-500" />}
            />
            <FormCardInput
              placeholder='Ex: https://www.azatys.fr'
              type='url'
              label="URL"
              name="site_url"
              rules={{ required: true, pattern: VALIDATION_URL }}
              errorType={errors?.site_url?.type}
              register={register}
              icon={<GlobeAltIcon className="w-4 h-4 text-gray-500" />}
            />
          </section>
          <footer>
            <FormBtn disabled={loading} label={loading ? "Enregistrement..." : "Enregistrer"} />
          </footer>
        </div>
      </form>
    </StandardDialog>
  );
};

/**
 * GETTINGSTARTEDMODAL COMPONENT
 * @param {{ open: boolean, actionOnClose: (b: boolean) => void}} props 
 * @returns {JSX.Element}
 */
export const GettingStartedModal = ({ open, actionOnClose }) => {
  const userContext = useSelector(selectUserInfo);
  const dispatch = useDispatch();
  const [show, setShow] = useState(open)


  const _close = useCallback(() => {
    setShow(false)
    actionOnClose(false)
    if (!localStorage.getItem(userContext?.user?.company_id) && userContext?.user?.admin && !!sessionStorage.getItem(PRODUCT_KEY)) {
      tour.start()
      dispatch(toggleLaunch())
      localStorage.setItem(userContext?.user?.company_id, 'Done')
    }
  }, [userContext])
  return (
    <StandardDialog open={show} onClose={() => { }}>
      <div className="bg-white rounded-3xl flex justify-between items-center">
        <img src={SARA} alt="" className="h-96" />
        <div className="text-left px-5 space-y-8 max-w-2xl flex flex-col justify-between min-h-[380px]">
          <h2 className="font-bold text-general-blue text-3xl">Il est temps de vous montrer comment paramétrer votre compte !</h2>
          <p className="text-gray-500 font-semibold h-full text-justify">
            <span className='text-general-yellow'>Suivez le guide !</span>
            <br /> <br />
          </p>
          <footer className="flex justify-end">
            <div className="w-[280px] animate-bounce hover:animate-none">
              <PrimaryBtn onClick={_close} label="C'est parti 🎉!" />
            </div>
          </footer>
        </div>
      </div>
    </StandardDialog>
  );
};